import React, { useRef, useEffect, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import 'katex/dist/katex.min.css'
import CodeSnippet from './CodeSnippet';
import ERDSnippet from './ERDSnippet';
import QuestionBlock from './QuestionBlock';
import RequirementsSnippet, { parseRequirements } from './RequirementsSnippet';
import { parseMCQ, parseShortAnswer } from '../../utils/questionParser';

/**
 * Remark plugin: collapse runs of adjacent fenced code blocks whose language
 * ends in ":runnable" into a single synthetic `multiRunnable` node.
 *
 * Authors can keep writing normal markdown — e.g.
 *
 *   ```python:runnable
 *   ...
 *   ```
 *
 *   ```matlab:runnable
 *   ...
 *   ```
 *
 * — and the renderer will mount one tabbed CodeSnippet that holds both, with
 * a tab strip for switching between them.
 *
 * A run of length 1 is left as-is so single-language blocks render exactly
 * the way they used to.
 */
function remarkGroupRunnable() {
  return (tree) => {
    if (!tree || tree.type !== 'root' || !Array.isArray(tree.children)) return;

    const isRunnableCode = (n) =>
      n &&
      n.type === 'code' &&
      typeof n.lang === 'string' &&
      n.lang.endsWith(':runnable') &&
      // Don't grab non-language fences that happen to contain ":runnable"
      !n.lang.startsWith('question:') &&
      !n.lang.startsWith('erd');

    const out = [];
    let group = [];

    const flush = () => {
      if (group.length === 0) return;
      if (group.length === 1) {
        out.push(group[0]);
      } else {
        const tabs = group.map(n => ({
          language: (n.lang || '').replace(/:runnable$/, ''),
          code: typeof n.value === 'string' ? n.value : ''
        }));
        out.push({
          type: 'multiRunnable',
          data: {
            hName: 'multirunnable',
            hProperties: {
              tabsJson: JSON.stringify(tabs)
            }
          }
        });
      }
      group = [];
    };

    for (const child of tree.children) {
      if (isRunnableCode(child)) {
        group.push(child);
      } else {
        flush();
        out.push(child);
      }
    }
    flush();

    tree.children = out;
  };
}

const PreviewPane = React.memo(({
  documentContent,
  scrollTop,
  onScroll,
  isStudent = false,
  onAskQuestion,
  studentProgress = {},
  onSubmitAnswer,
  documentId = null,
  classContext = null,
  erdState = null,
  environmentId = null,
  username = null,
  readOnly = false,
  onNotationChange = null,      // (originalContent: string, newNotation: string) => void
  onRequestFeedback = null      // () => void — called from ERDSnippet "Get AI Feedback" button
}) => {
  const previewRef = useRef(null);
  const isSettingScrollRef = useRef(false);
  const lastScrollTopRef = useRef(null);
  const lastEmittedScrollRef = useRef(null);
  const [bubble, setBubble] = useState(null);
  // Tracks the most recently rendered requirements env so ERD blocks can inherit it
  const lastReqEnvRef = useRef(null);

  const handlePreviewScroll = (e) => {
    if (isSettingScrollRef.current || !onScroll) return;

    const element = e.target;
    const currentScrollTop = element.scrollTop;

    const now = Date.now();
    if (lastEmittedScrollRef.current && (now - lastEmittedScrollRef.current) < 50) {
      return;
    }

    lastEmittedScrollRef.current = now;
    const scrollPercentage = currentScrollTop / (element.scrollHeight - element.clientHeight) || 0;
    onScroll(scrollPercentage);
  };

  useEffect(() => {
    if (previewRef.current && scrollTop !== undefined && !isSettingScrollRef.current) {
      const element = previewRef.current;
      const maxScroll = Math.max(0, element.scrollHeight - element.clientHeight);
      const targetScrollTop = scrollTop * maxScroll;

      if (lastScrollTopRef.current === targetScrollTop) {
        return;
      }

      if (Math.abs(element.scrollTop - targetScrollTop) > 5) {
        isSettingScrollRef.current = true;
        lastScrollTopRef.current = targetScrollTop;
        element.scrollTop = targetScrollTop;

        setTimeout(() => {
          isSettingScrollRef.current = false;
        }, 100);
      }
    }
  }, [scrollTop]);

  const handleMouseUp = () => {
    setTimeout(() => {
      const selection = window.getSelection();
      const text = selection?.toString().trim();

      if (text) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        setBubble({
          text,
          x: rect.left + rect.width / 2,
          y: rect.top - 10
        });
      } else {
        setBubble(null);
      }
    }, 10);
  };

  const handleAskClick = () => {
    if (bubble && onAskQuestion) {
      onAskQuestion(bubble.text);
      window.getSelection()?.removeAllRanges();
      setBubble(null);
    }
  };

  // Pre-parse all ```requirements:env blocks from the raw document content so that
  // when ERD blocks render they can synchronously look up their linked requirements.
  // Map key: envname (string) → parsed requirements array.
  const requirementsMap = useMemo(() => {
    const map = {};
    if (!documentContent) return map;
    const regex = /```requirements:(\S+)\n([\s\S]*?)```/g;
    let match;
    while ((match = regex.exec(documentContent)) !== null) {
      const env = match[1];
      const body = match[2];
      map[env] = parseRequirements(body);
    }
    return map;
  }, [documentContent]);

  // Memoize the markdown components to prevent CodeSnippet from being recreated on bubble state changes
  const markdownComponents = useMemo(() => ({
    p: ({ children }) => (
      <p className="mb-4 text-gray-800 leading-relaxed">
        {children}
      </p>
    ),
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold mt-6 mb-4 text-gray-900 border-b-2 border-gray-300 pb-3">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-semibold mt-6 mb-4 text-gray-800 border-b border-gray-200 pb-2">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-semibold mt-6 mb-3 text-gray-800">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-base font-semibold mt-5 mb-2 text-gray-800">
        {children}
      </h4>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-blue-300 pl-4 py-2 my-4 bg-blue-50 italic text-gray-700">
        {children}
      </blockquote>
    ),
    code: ({ inline, className, children }) => {
      // Inline code
      if (inline) {
        return (
          <code className="bg-gray-100 border border-gray-200 px-2 py-1 rounded text-sm font-mono">
            {children}
          </code>
        );
      }

      // For non-inline code, just return the code element with class info
      // The pre wrapper will be handled by the pre component
      return (
        <code className={className ? `text-sm font-mono whitespace-pre ${className}` : "text-sm font-mono whitespace-pre"}>
          {children}
        </code>
      );
    },
    pre: ({ children }) => {
      // Get the code element from children
      const codeChild = React.Children.toArray(children)[0];
      const className = codeChild?.props?.className || '';
      const codeContent = codeChild?.props?.children;

      // Check for question blocks (e.g., ```question:mcq or ```question:short)
      const questionMatch = className.match(/language-question:(mcq|short|code)/);
      if (questionMatch) {
        const questionType = questionMatch[1];

        // Parse the question content directly (same as CodeSnippet does with code)
        const rawContent = typeof codeContent === 'string'
          ? codeContent
          : (codeContent?.props?.children || '');

        let questionData = null;
        if (questionType === 'mcq') {
          questionData = parseMCQ(rawContent);
        } else if (questionType === 'short') {
          questionData = parseShortAnswer(rawContent);
        }

        if (questionData) {
          // Get student's previous answer for this question
          const questionId = questionData.id;
          const progressData = studentProgress?.[questionId];

          // Format previous answer for QuestionBlock
          const studentAnswer = progressData ? {
            selectedOptions: progressData.selected_options || [],
            shortAnswer: progressData.text_answer || ''
          } : null;

          return (
            <QuestionBlock
              questionData={questionData}
              isStudent={isStudent}
              studentAnswer={studentAnswer}
              showFeedback={!!progressData}
              readOnly={readOnly}
              onSubmitAnswer={readOnly ? null : (qId, answer) => {
                if (onSubmitAnswer) {
                  // Call the parent handler with full question data
                  onSubmitAnswer(
                    qId,
                    answer,
                    questionData.questionType,
                    questionData.rubric
                  );
                }
              }}
            />
          );
        }
      }

      // Check for requirements blocks (e.g., ```requirements:hospital)
      const requirementsMatch = className && className.match(/language-requirements:(\S+)/);
      if (requirementsMatch) {
        const reqEnv = requirementsMatch[1];
        lastReqEnvRef.current = reqEnv; // remember for any ERD block that follows
        const reqContent = typeof codeContent === 'string'
          ? codeContent
          : (codeContent?.props?.children || '');
        return (
          <RequirementsSnippet
            key={`req-${reqEnv}`}
            content={reqContent}
            env={reqEnv}
            isStudent={isStudent}
          />
        );
      }

      // Check for ERD editor blocks (e.g., ```erd or ```erd:editor or ```erd:envname)
      const isERD = className && (className.includes('language-erd:editor') || className.includes('language-erd'));
      if (isERD) {
        const erdContent = typeof codeContent === 'string'
          ? codeContent
          : (codeContent?.props?.children || '');
        const erdKey = `erd-snippet-${erdContent.slice(0, 50).replace(/\s/g, '-')}-${erdContent.length}`;

        // Parse modifiers from erd info string: erd:envname:editable:savable
        // Parts after "erd:" are split by ":" — reserved words: "editor", "editable", "savable"
        const erdInfoStr = className.replace('language-erd', '').replace(/^:/, '');
        const erdParts = erdInfoStr ? erdInfoStr.split(':') : [];
        const isEditable = erdParts.includes('editable');
        const isSavable = erdParts.includes('savable');
        const erdEnv = erdParts.find(p => !['editable', 'editor', 'savable'].includes(p)) || null;
        // Fall back to the most recently seen requirements block env
        const resolvedEnv = erdEnv || lastReqEnvRef.current || null;
        const linkedRequirements = resolvedEnv ? (requirementsMap[resolvedEnv] || []) : [];

        return (
          <ERDSnippet
            key={erdKey}
            className={className}
            isStudent={isStudent}
            snippetId={erdKey}
            documentId={documentId}
            classContext={classContext}
            erdState={erdState}
            environmentId={resolvedEnv || environmentId}
            username={username}
            readOnly={readOnly}
            env={resolvedEnv}
            isEditable={isEditable}
            isSavable={isSavable}
            requirements={linkedRequirements}
            onNotationChange={onNotationChange
              ? (newNotation) => onNotationChange(erdContent, newNotation)
              : null}
            onRequestFeedback={onRequestFeedback || null}
          >
            {erdContent}
          </ERDSnippet>
        );
      }

      // Check for runnable code
      const isRunnable = className && className.includes(':runnable');
      if (isRunnable) {
        // Create a stable key based on code content to preserve state across rerenders
        const codeString = typeof codeContent === 'string'
          ? codeContent
          : (codeContent?.props?.children || '');
        // Simple hash function for key generation
        const snippetKey = `snippet-${codeString.slice(0, 50).replace(/\s/g, '-')}-${codeString.length}`;

        return (
          <CodeSnippet
            key={snippetKey}
            className={className}
            isStudent={isStudent}
            snippetId={snippetKey}
            documentId={documentId}
            classContext={classContext}
            readOnly={readOnly}
          >
            {codeContent}
          </CodeSnippet>
        );
      }

      // Regular code block
      return (
        <pre className="bg-gray-100 border border-gray-200 p-4 rounded-lg mt-4 mb-4 overflow-x-auto">
          {children}
        </pre>
      );
    },
    ul: ({ children }) => (
      <ul className="list-disc pl-6 my-4 space-y-1">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-6 my-4 space-y-1">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-gray-800">
        {children}
      </li>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        className="text-blue-600 hover:text-blue-800 underline transition-colors duration-200"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    img: ({ src, alt }) => (
      <div className="my-4">
        <img
          src={src}
          alt={alt}
          className="max-w-full h-auto rounded-lg shadow-sm border border-gray-200"
          loading="lazy"
        />
      </div>
    ),
    hr: () => (
      <hr className="my-6 border-t-2 border-gray-200" />
    ),
    strong: ({ children }) => (
      <strong className="font-bold text-gray-900">
        {children}
      </strong>
    ),
    em: ({ children }) => (
      <em className="italic text-gray-700">
        {children}
      </em>
    ),
    // Synthetic element produced by remarkGroupRunnable when two or more
    // adjacent ":runnable" fences are merged. The tabs payload travels as
    // a JSON-encoded prop so it survives the mdast → hast → React pipeline.
    multirunnable: (props) => {
      const dataTabs =
        props.tabsJson ||
        props.tabsjson ||
        props['data-tabs'] ||
        '[]';
      let tabs = [];
      try {
        tabs = JSON.parse(dataTabs);
      } catch (err) {
        console.warn('[PreviewPane] Failed to parse multi-runnable tabs:', err);
        tabs = [];
      }

      // Stable key based on combined code so React keeps editor state across
      // re-renders (e.g. when only an unrelated part of the document changed).
      const concat = tabs.map(t => t.code || '').join('||');
      const snippetKey = `multi-snippet-${concat.slice(0, 50).replace(/\s/g, '-')}-${concat.length}`;

      return (
        <CodeSnippet
          key={snippetKey}
          tabs={tabs}
          isStudent={isStudent}
          snippetId={snippetKey}
          documentId={documentId}
          classContext={classContext}
          readOnly={readOnly}
        />
      );
    }
  }), [isStudent, studentProgress, onSubmitAnswer, readOnly, documentId, classContext, onNotationChange, onRequestFeedback, requirementsMap]);

  return (
    <div className="w-full h-full relative bg-white">
      <div
        ref={previewRef}
        className="w-full h-full overflow-y-auto"
        onScroll={handlePreviewScroll}
        onMouseUp={readOnly ? undefined : handleMouseUp}
      >
        {documentContent ? (
          <div
            className="p-6 min-h-full"
            style={{
              fontFamily: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
              fontSize: '16px',
              lineHeight: '1.7',
              color: '#1f2937'
            }}
          >
            <div className="prose prose-lg max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkMath, remarkGroupRunnable]}
                rehypePlugins={[rehypeRaw, rehypeKatex]}
                components={markdownComponents}
              >
                {documentContent}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
            <div className="text-center p-12 max-w-md">
              <div className="relative mb-8">
                <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg transform transition-transform duration-300 hover:scale-105">
                  {isStudent ? (
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  ) : (
                    <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                </div>
              </div>

              {isStudent ? (
                <>
                  <div className="space-y-3 text-gray-600 leading-relaxed">
                    <p className="text-lg">
                      Select a document from the <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-md">Table of Contents</span> to view beautifully formatted course content.
                    </p>

                    <div className="grid grid-cols-1 gap-3 py-4">
                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                        <span className="font-medium">Lecture notes and study guides</span>
                      </div>
                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="font-medium">Assignment instructions</span>
                      </div>
                      <div className="flex items-center space-x-3 text-sm text-gray-600">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <span className="font-medium">Interactive learning resources</span>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/50 rounded-xl shadow-sm">
                      <div className="flex items-start space-x-3">
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-blue-800 mb-1">Read-Only Access</p>
                          <p className="text-xs text-blue-700">Course materials are view-only. Use the Q&A panel to ask questions and engage with the content.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Document Preview
                  </h3>

                  <div className="space-y-3 text-gray-600 leading-relaxed">
                    <p className="text-lg">
                      Select a document from the <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-md">Table of Contents</span> to view its formatted content here.
                    </p>

                    <div className="flex items-center justify-center space-x-4 py-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>Rich markdown support</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>LaTeX math rendering</span>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-white/70 backdrop-blur-sm border border-white/20 rounded-xl shadow-sm">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold text-blue-600">Tip:</span> Switch to <span className="font-medium bg-slate-100 px-2 py-1 rounded text-slate-700">Write</span> mode to edit documents, or use the AI assistant to generate new content.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {bubble && (
        <div
          style={{
            position: 'fixed',
            left: bubble.x,
            top: bubble.y,
            transform: 'translate(-50%, -100%)',
            zIndex: 9999
          }}
        >
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleAskClick}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-lg hover:bg-blue-50 text-sm font-medium text-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Ask Question</span>
          </button>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  const checks = {
    documentContent: prevProps.documentContent === nextProps.documentContent,
    isStudent: prevProps.isStudent === nextProps.isStudent,
    readOnly: prevProps.readOnly === nextProps.readOnly,
    onNotationChange: prevProps.onNotationChange === nextProps.onNotationChange,
    onRequestFeedback: prevProps.onRequestFeedback === nextProps.onRequestFeedback,
  };
  const equal = Object.values(checks).every(Boolean);
  if (!equal) {
    const changed = Object.entries(checks).filter(([, v]) => !v).map(([k]) => k);
    console.warn('[PreviewPane] re-rendering — changed props:', changed);
  }
  return equal;
});

export default PreviewPane;
